import React, { useEffect } from "react";
import {
  FormSection,
  SectionTitle,
  FormGroup,
  Label,
  Select,
  Input,
  TextArea,
  ErrorMessage,
  InfoMessage,
  MedicationItem,
  MedicationHeader,
  RemoveButton,
  MedicineDetailDisplay,
  AddMoreButton
} from "../../styles/OrderFormStyles";
import SearchableMedicineDropdown from "./SearchableMedicineDropdown"; // Import our new component

const OrderRequestDetails = ({
  formData,
  setFormData,
  errors,
  loading,
  states,
  pharmacies,
  medicines,
  providersForState,
  loadingProviders,
  handleInputChange,
  handlePharmacyChange,
  handleMedicationChange,
  addMedicine,
  removeMedicine,
  loadPharmaciesAndMedicines,
  editMode,
  originalOrderData
}) => {
  // Find the matching state from patient address
  const matchedState = states.find(
    (state) =>
      state.state_name.toLowerCase() ===
        formData.patientAddress?.state?.toLowerCase() ||
      state.state_abbreviation.toLowerCase() ===
        formData.patientAddress?.state?.toLowerCase()
  );

  useEffect(() => {
    // Handle initialization in edit mode
    if (editMode && originalOrderData) {
      // If in edit mode and we have original order data, set pharmacy ID
      if (
        originalOrderData.pharmacy &&
        originalOrderData.pharmacy.pharmacy_id &&
        !formData.pharmacyId
      ) {
        const pharmacyId = originalOrderData.pharmacy.pharmacy_id.toString();

        // Find the matching state for this pharmacy
        const stateId =
          formData.stateId ||
          (originalOrderData.shipping_address?.state &&
            states
              .find(
                (s) =>
                  s.state_abbreviation ===
                  originalOrderData.shipping_address.state
              )
              ?.state_id.toString());

        if (stateId) {
          // Load pharmacies and medicines for this state if needed
          loadPharmaciesAndMedicines(stateId);

          // Update form data with pharmacy and state
          setFormData((prev) => ({
            ...prev,
            pharmacyId,
            stateId,
            orderType: originalOrderData.order_type || "new"
          }));
        }
      }
    }
  }, [
    editMode,
    originalOrderData,
    states,
    formData.pharmacyId,
    formData.stateId,
    loadPharmaciesAndMedicines,
    setFormData
  ]);

  // Filter medicines based on selected pharmacy
  const getFilteredMedicinesForPharmacy = (pharmacyId) => {
    if (!pharmacyId) return [];
    return medicines.filter(
      (med) => med.pharmacy_id?.toString() === pharmacyId.toString()
    );
  };




  return (
    <FormSection>
      <SectionTitle>Order Request Details</SectionTitle>

      <FormGroup>
        <Label htmlFor="orderType">Order Type</Label>
        <Select
          id="orderType"
          name="orderType"
          value={formData.orderType}
          onChange={handleInputChange}
        >
          <option value="new">New Order</option>
          <option value="refill">Refill</option>
        </Select>
      </FormGroup>

      <FormGroup>
        <Label htmlFor="stateId">State</Label>
        <Select
          id="stateId"
          name="stateId"
          value={matchedState?.state_id || formData.stateId || ""}
          onChange={(e) => {
            const value = e.target.value;
            setFormData((prevData) => ({
              ...prevData,
              stateId: value,
              pharmacyId: "",
              medicines: [
                {
                  id: "",
                  name: "",
                  quantity: 1,
                  price: 0,
                  description: "",
                  in_stock: true,
                  medicine_sku: "",
                  medicine_id: "",
                  dosage_instructions: [],
                  all_dosage_instructions: [],
                  variants: []
                }
              ]
            }));

            if (value) {
              loadPharmaciesAndMedicines(value);
            }
          }}
          // disabled={loading.loadingStates}
          disabled={true}
        >
          <option key="default-state" value="">
            Select State
          </option>
          {states.map((state, index) => (
            <option
              key={`state-${index}`}
              value={state.state_id?.toString() || state.id?.toString()}
            >
              {state.state_name}
            </option>
          ))}
        </Select>
        {errors.stateId && <ErrorMessage>{errors.stateId}</ErrorMessage>}
        {loading.loadingStates && <div>Loading states...</div>}
      </FormGroup>

      <FormGroup>
        <Label htmlFor="pharmacyId">Pharmacy</Label>
        <Select
          id="pharmacyId"
          name="pharmacyId"
          value={formData.pharmacyId || ""}
          onChange={handlePharmacyChange}
          disabled={loading.loadingPharmacies}
        >
          <option key="default-pharmacy" value="">
            Select Pharmacy
          </option>
          {[...pharmacies]
            .sort((a, b) => {
              const rank = (name = "") => {
                const n = name.toLowerCase();
                if (n.includes("miller")) return 0;
                if (n.includes("nie")) return 1;
                return 2;
              };
              return rank(a.name) - rank(b.name);
            })
            .map((pharmacy, index) => {
              const details = [pharmacy.address, pharmacy.phone, pharmacy.email].filter(Boolean).join(" | ");
              return (
                <option
                  key={`pharmacy-${pharmacy.id || pharmacy.pharmacy_id || index}`}
                  value={pharmacy.id || pharmacy.pharmacy_id || ""}
                >
                  {pharmacy.name}{details ? ` — ${details}` : ""}
                </option>
              );
            })}
        </Select>
        {errors.pharmacyId && <ErrorMessage>{errors.pharmacyId}</ErrorMessage>}
        {loading.loadingPharmacies && <div>Loading pharmacies...</div>}
        {formData.selectedAddressId &&
          pharmacies.length === 0 &&
          !loading.loadingPharmacies && (
            <ErrorMessage>
              No pharmacies available in the selected state.
            </ErrorMessage>
          )}
        {!formData.selectedAddressId && (
          <InfoMessage>
            Pharmacies are loaded from the patient's address state. The patient
            has no address on file - add one in the Shipping Address section
            below.
          </InfoMessage>
        )}
      </FormGroup>

      <FormGroup>
        <Label htmlFor="providerId">Provider</Label>
        {!formData.pharmacyId ? (
          <InfoMessage>Please select a pharmacy first</InfoMessage>
        ) : (
          <>
            <Select
              id="providerId"
              name="providerId"
              value={formData.providerId || ""}
              onChange={(e) => {
                const value = e.target.value;
                setFormData((prev) => ({ ...prev, providerId: value }));
              }}
              disabled={loadingProviders}
            >
              <option value="">Select Provider</option>
              {(providersForState || []).map((p) => (
                <option key={p.provider_id} value={p.provider_id}>
                  {p.name ||
                    p.display_name ||
                    `${p.first_name || ""} ${p.last_name || ""}`.trim()}
                  {p.credential ? `, ${p.credential}` : ""}
                </option>
              ))}
            </Select>
            {loadingProviders && <div>Loading providers...</div>}
            {errors.providerId && <ErrorMessage>{errors.providerId}</ErrorMessage>}
            {!loadingProviders &&
              formData.pharmacyId &&
              (providersForState || []).length === 0 && (
                <ErrorMessage>
                  No providers available for the selected pharmacy.
                </ErrorMessage>
              )}
          </>
        )}
      </FormGroup>

      <FormGroup>
        <Label>Medicines</Label>
        {!formData.pharmacyId && formData.stateId && (
          <InfoMessage>Please select a pharmacy to add medicines</InfoMessage>
        )}
        {formData.medicines.map((medicine, index) => (
          <MedicationItem key={`med-item-${index}`}>
            <MedicationHeader>
              <h4>Medicine {index + 1}</h4>
              {formData.medicines.length > 1 && (
                <RemoveButton
                  type="button"
                  onClick={() => removeMedicine(index)}
                >
                  Remove
                </RemoveButton>
              )}
            </MedicationHeader>

            <FormGroup>
              <Label htmlFor={`medicines[${index}].medicine_id`}>
                Medicine
              </Label>
              {/* Replace the standard Select with our SearchableMedicineDropdown */}
              <SearchableMedicineDropdown
                options={getFilteredMedicinesForPharmacy(formData.pharmacyId)}
                value={medicine.medicine_id || ""}
                onChange={(e) =>
                  handleMedicationChange(index, "medicine_id", e.target.value)
                }
                placeholder="Search or select a medicine..."
                disabled={loading.loadingMedicines || !formData.pharmacyId}
                error={errors[`medicines[${index}].medicine_id`]}
                name={`medicines[${index}].medicine_id`}
              />
              {errors[`medicines[${index}].medicine_id`] && (
                <ErrorMessage>
                  {errors[`medicines[${index}].medicine_id`]}
                </ErrorMessage>
              )}
            </FormGroup>

            <FormGroup>
              <Label>Variant</Label>
              <Select
                name={`medicines[${index}].variant_id`}
                value={medicine.variant_id || ""}
                onChange={(e) =>
                  handleMedicationChange(index, "variant_id", e.target.value)
                }
                disabled={loading.loadingMedicines}
              >
                <option value="">Select Variant</option>
                {medicine.variants?.map((variant) => (
                  <option key={variant.variant_id} value={variant.variant_id}>
                    {variant.quantity} {variant.unit}
                    {variant.note && ` (${variant.note})`}
                  </option>
                ))}
              </Select>
              {errors[`medicines[${index}].variant_id`] && (
                <ErrorMessage>
                  {errors[`medicines[${index}].variant_id`]}
                </ErrorMessage>
              )}
            </FormGroup>

            <FormGroup>
              <Label htmlFor={`medicines[${index}].price`}>Price ($)</Label>
              <Input
                type="number"
                name={`medicines[${index}].price`}
                value={medicine.price}
                onChange={(e) =>
                  handleMedicationChange(index, "price", e.target.value)
                }
                min="0"
                step="0.01"
              />
              {errors[`medicines[${index}].price`] && (
                <ErrorMessage>
                  {errors[`medicines[${index}].price`]}
                </ErrorMessage>
              )}
            </FormGroup>

            <FormGroup>
              <Label htmlFor={`medicines[${index}].compounding_context`}>
                Reason for Compounding Context
              </Label>
              <TextArea
                name={`medicines[${index}].compounding_context`}
                value={medicine.compounding_context || ""}
                onChange={(e) =>
                  handleMedicationChange(
                    index,
                    "compounding_context",
                    e.target.value
                  )
                }
                rows={3}
                placeholder="Reason this medication is being compounded for this patient"
              />
              {errors[`medicines[${index}].compounding_context`] && (
                <ErrorMessage>
                  {errors[`medicines[${index}].compounding_context`]}
                </ErrorMessage>
              )}
            </FormGroup>

            <FormGroup>
              <Label>Dosage Instructions</Label>
              
              {/* Tab-style toggle between existing and custom dosage instructions */}
              <div style={{ 
                display: 'flex', 
                marginBottom: '16px', 
                border: '1px solid #3a3a3a', 
                borderRadius: '8px', 
                overflow: 'hidden',
                backgroundColor: '#2a2a2a'
              }}>
                <button
                  type="button"
                  onClick={() => {
                    const updatedMedicines = [...formData.medicines];
                    updatedMedicines[index] = {
                      ...updatedMedicines[index],
                      useCustomDosage: false,
                      customDosageInstructions: [],
                      dosage_instruction_id: ""
                    };
                    setFormData(prev => ({ ...prev, medicines: updatedMedicines }));
                  }}
                  style={{
                    flex: 1,
                    padding: '12px 16px',
                    border: 'none',
                    backgroundColor: !medicine.useCustomDosage ? '#6366f1' : 'transparent',
                    color: !medicine.useCustomDosage ? '#ffffff' : '#aaaaaa',
                    fontSize: '0.875rem',
                    fontWeight: !medicine.useCustomDosage ? '600' : '400',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    borderRight: '1px solid #3a3a3a'
                  }}
                  onMouseEnter={(e) => {
                    if (medicine.useCustomDosage) {
                      e.target.style.backgroundColor = '#3a3a3a';
                      e.target.style.color = '#ffffff';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (medicine.useCustomDosage) {
                      e.target.style.backgroundColor = 'transparent';
                      e.target.style.color = '#aaaaaa';
                    }
                  }}
                >
                  Use Existing Dosage Instructions
                </button>
                <button
                  type="button"
                  onClick={() => {
                    const updatedMedicines = [...formData.medicines];
                    updatedMedicines[index] = {
                      ...updatedMedicines[index],
                      useCustomDosage: true,
                      dosage_instruction_id: "",
                      customDosageInstructions: [""]
                    };
                    setFormData(prev => ({ ...prev, medicines: updatedMedicines }));
                  }}
                  style={{
                    flex: 1,
                    padding: '12px 16px',
                    border: 'none',
                    backgroundColor: medicine.useCustomDosage ? '#6366f1' : 'transparent',
                    color: medicine.useCustomDosage ? '#ffffff' : '#aaaaaa',
                    fontSize: '0.875rem',
                    fontWeight: medicine.useCustomDosage ? '600' : '400',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    if (!medicine.useCustomDosage) {
                      e.target.style.backgroundColor = '#3a3a3a';
                      e.target.style.color = '#ffffff';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!medicine.useCustomDosage) {
                      e.target.style.backgroundColor = 'transparent';
                      e.target.style.color = '#aaaaaa';
                    }
                  }}
                >
                  Add Custom Dosage Instructions
                </button>
              </div>

              {/* Existing dosage instructions dropdown */}
              {!medicine.useCustomDosage && (
                <>
                  <Select
                    name={`medicines[${index}].dosage_instruction_id`}
                    value={medicine.dosage_instruction_id || ""}
                    onChange={(e) =>
                      handleMedicationChange(
                        index,
                        "dosage_instruction_id",
                        e.target.value
                      )
                    }
                    disabled={loading.loadingMedicines}
                  >
                    <option value="">Select Dosage Instruction</option>
                    {/* Regular dosage instructions */}
                    {medicine.dosage_instructions?.map((instruction) => (
                      <option
                        key={instruction.dosage_instruction_id}
                        value={instruction.dosage_instruction_id}
                      >
                        {instruction.instruction}
                      </option>
                    ))}
                  </Select>
                  {errors[`medicines[${index}].dosage_instruction_id`] && (
                    <ErrorMessage>
                      {errors[`medicines[${index}].dosage_instruction_id`]}
                    </ErrorMessage>
                  )}

                  {medicine.dosage_instruction_id && (
                    <InfoMessage style={{ color: "green", marginTop: "5px" }}>
                      Selected:{" "}
                      {(() => {
                        const regularInstruction = medicine.dosage_instructions?.find(
                          (di) =>
                            di.dosage_instruction_id === parseInt(medicine.dosage_instruction_id)
                        );
                        return regularInstruction?.instruction || "Not found";
                      })()}
                    </InfoMessage>
                  )}
                </>
              )}

              {/* Custom dosage instructions section */}
              {medicine.useCustomDosage && (
                <div style={{ border: '1px solid #3a3a3a', borderRadius: '8px', padding: '16px', marginTop: '12px' }}>
                  <h5 style={{ color: '#6366f1', marginBottom: '12px', fontSize: '1rem' }}>
                    Custom Dosage Instructions for {medicine.medicine_name || `Medicine ${index + 1}`}
                  </h5>
                  
                  {(medicine.customDosageInstructions || []).map((instruction, instructionIndex) => (
                    <div key={`custom-instruction-${instructionIndex}`} style={{ marginBottom: '12px', padding: '12px', border: '1px solid #2a2a2a', borderRadius: '6px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                        <Label style={{ margin: 0 }}>
                          Custom Instruction {instructionIndex + 1}
                        </Label>
                        {(medicine.customDosageInstructions || []).length > 1 && (
                          <RemoveButton
                            type="button"
                            onClick={() => {
                              const updatedMedicines = [...formData.medicines];
                              const updatedInstructions = [...(updatedMedicines[index].customDosageInstructions || [])];
                              updatedInstructions.splice(instructionIndex, 1);
                              updatedMedicines[index] = {
                                ...updatedMedicines[index],
                                customDosageInstructions: updatedInstructions
                              };
                              setFormData(prev => ({ ...prev, medicines: updatedMedicines }));
                            }}
                            style={{ padding: '4px 8px', fontSize: '0.75rem' }}
                          >
                            Remove
                          </RemoveButton>
                        )}
                      </div>
                      
                      <Input
                        type="text"
                        name={`medicines[${index}].customDosageInstructions[${instructionIndex}]`}
                        value={instruction || ""}
                        onChange={(e) => {
                          const updatedMedicines = [...formData.medicines];
                          const updatedInstructions = [...(updatedMedicines[index].customDosageInstructions || [])];
                          updatedInstructions[instructionIndex] = e.target.value;
                          updatedMedicines[index] = {
                            ...updatedMedicines[index],
                            customDosageInstructions: updatedInstructions
                          };
                          setFormData(prev => ({ ...prev, medicines: updatedMedicines }));
                        }}
                        placeholder="Enter custom dosage instruction (e.g., Take 1 tablet twice daily with meals)"
                        style={{ width: '100%' }}
                      />
                      {errors[`medicines[${index}].customDosageInstructions[${instructionIndex}]`] && (
                        <ErrorMessage>
                          {errors[`medicines[${index}].customDosageInstructions[${instructionIndex}]`]}
                        </ErrorMessage>
                      )}
                    </div>
                  ))}

                  {/* <AddMoreButton
                    type="button"
                    onClick={() => {
                      const updatedMedicines = [...formData.medicines];
                      const currentInstructions = updatedMedicines[index].customDosageInstructions || [];
                      updatedMedicines[index] = {
                        ...updatedMedicines[index],
                        customDosageInstructions: [...currentInstructions, ""]
                      };
                      setFormData(prev => ({ ...prev, medicines: updatedMedicines }));
                    }}
                    disabled={(medicine.customDosageInstructions || []).length >= 10}
                    style={{ padding: '6px 12px', fontSize: '0.875rem' }}
                  >
                    + Add Another Custom Instruction
                  </AddMoreButton> */}

                  {(medicine.customDosageInstructions || []).length > 0 && (
                    <InfoMessage style={{ color: "green", marginTop: "12px" }}>
                      {(medicine.customDosageInstructions || []).filter(inst => inst && inst.trim()).length} custom instruction(s) added
                    </InfoMessage>
                  )}
                </div>
              )}
            </FormGroup>
          </MedicationItem>
        ))}

        {errors.medicines && <ErrorMessage>{errors.medicines}</ErrorMessage>}
      </FormGroup>

      <AddMoreButton
        type="button"
        onClick={addMedicine}
        disabled={formData.medicines.length >= 5 || !formData.pharmacyId}
      >
        + Add Another Medicine
      </AddMoreButton>
    </FormSection>
  );
};

export default OrderRequestDetails;